import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import { getToken, setToken, removeToken, isTokenExpired } from '../utils/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check authentication status on mount
  const checkAuth = useCallback(async () => {
    const token = getToken();
    
    if (!token) {
      setIsAuthenticated(false);
      setAdmin(null);
      setLoading(false);
      return false;
    }

    // Check if token is expired
    if (isTokenExpired(token)) {
      removeToken();
      setIsAuthenticated(false);
      setAdmin(null);
      setLoading(false);
      return false;
    }

    // Verify token with server
    try {
      const response = await authService.verifyToken();
      if (response.success && response.data.admin) {
        setIsAuthenticated(true);
        setAdmin(response.data.admin);
        setLoading(false);
        return true;
      } else {
        removeToken();
        setIsAuthenticated(false);
        setAdmin(null);
        setLoading(false);
        return false;
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      removeToken();
      setIsAuthenticated(false);
      setAdmin(null);
      setLoading(false);
      return false;
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login function
  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      
      if (response.success && response.data.token) {
        setToken(response.data.token);
        setIsAuthenticated(true);
        setAdmin(response.data.admin);
        return { success: true, admin: response.data.admin };
      } else {
        return { success: false, message: response.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.message || 'Login failed. Please check your credentials.' 
      };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      removeToken();
      setIsAuthenticated(false);
      setAdmin(null);
    }
  };

  const value = {
    isAuthenticated,
    admin,
    loading,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;

